// SKI BUTLERS WHISTLER (staging, founder brief 2026-10-21) — THE PUBLIC RENTAL TERMS A QUOTE PROVES, AND THE GUESTS AN ORDER CARRIES.
//
// snowclub-be prices a rental vendor's line per inclusive day only when that vendor's persisted policy opts it in, and says so on
// the quote line as `rental` (services/rentals/ski-butlers-whistler.ts `publicSbwLine`). This module is the browser's side of that
// contract, and it is deliberately NARROW:
//
//   · `sanitizeRentalTerms` keeps a bounded allowlist of the public terms and refuses (null) anything malformed — a quote whose
//     rental terms can't be read is never treated as "not a rental", and nothing outside the allowlist (an internal price note, a
//     commission, guest data) is ever passed on.
//   · Guests are page-memory only. They are validated here, shaped into the backend's entries (one full entry per person, `{key}`
//     references after it) and sent ONLY with the order request. They are never stored, logged, put in a URL, an event or a quote.

import type { DesignOrderProduct } from "@/lib/design-pay-order";

export const RENTAL_GUEST_FIELDS = ["name", "phone", "email", "age", "heightCm", "weightKg", "shoeSize", "ability"] as const;
export type RentalGuestField = (typeof RENTAL_GUEST_FIELDS)[number];
export const RENTAL_ABILITIES = ["first-timer", "beginner", "intermediate", "advanced", "expert"] as const;
export const RENTAL_ABILITY_LABEL: Record<(typeof RENTAL_ABILITIES)[number], string> = {
  "first-timer": "First timer",
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
  expert: "Expert",
};

/** The public requirements and terms of one rented line, exactly as the backend quote stated them. */
export interface RentalTerms {
  kind: "rental" | "addon";
  startDate: string;
  endDate: string;
  days: number;
  perDay: number;
  currency: string;
  service: string;
  bookingContactEmail: string;
  pickupWindow: { date: string; from: string; to: string; timeZone: string };
  noticeHours: number;
  cancellation: { freeUntil: string; lateFeePercent: number };
  guestsRequired: number;
  requiredGuestFields: RentalGuestField[];
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const CLOCK = /^\d{2}:\d{2}$/;
const EMAIL = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const shortStr = (v: unknown, max = 80): v is string => typeof v === "string" && v.trim() !== "" && v.length <= max;
const isGuestField = (f: unknown): f is RentalGuestField => (RENTAL_GUEST_FIELDS as readonly unknown[]).includes(f);
const wholeIn = (v: unknown, min: number, max: number): v is number => typeof v === "number" && Number.isInteger(v) && v >= min && v <= max;

/** The allowlisted public terms, or null when ANY of them is missing or malformed (the caller then fails closed). */
export function sanitizeRentalTerms(raw: unknown): RentalTerms | null {
  if (!isObj(raw)) return null;
  const { kind, startDate, endDate, days, perDay, currency, service, bookingContactEmail, pickupWindow: pw, noticeHours, cancellation: c, guestsRequired, requiredGuestFields } = raw;
  if (kind !== "rental" && kind !== "addon") return null;
  if (typeof startDate !== "string" || !ISO_DATE.test(startDate) || typeof endDate !== "string" || !ISO_DATE.test(endDate)) return null;
  if (!wholeIn(days, 1, 366) || typeof perDay !== "number" || !Number.isFinite(perDay) || perDay < 0) return null;
  if (!shortStr(currency, 8) || !shortStr(service) || typeof bookingContactEmail !== "string" || !EMAIL.test(bookingContactEmail) || bookingContactEmail.length > 254) return null;
  if (!isObj(pw) || typeof pw.date !== "string" || !ISO_DATE.test(pw.date) || typeof pw.from !== "string" || !CLOCK.test(pw.from) || typeof pw.to !== "string" || !CLOCK.test(pw.to) || !shortStr(pw.timeZone)) return null;
  if (!wholeIn(noticeHours, 0, 24 * 60)) return null;
  if (!isObj(c) || typeof c.freeUntil !== "string" || !Number.isFinite(Date.parse(c.freeUntil)) || typeof c.lateFeePercent !== "number" || !Number.isFinite(c.lateFeePercent) || c.lateFeePercent < 0 || c.lateFeePercent > 100) return null;
  if (!wholeIn(guestsRequired, 1, 100)) return null;
  const fields: unknown[] = Array.isArray(requiredGuestFields) ? (requiredGuestFields as unknown[]) : [];
  if (!Array.isArray(requiredGuestFields) || !fields.every(isGuestField)) return null;
  return {
    kind, startDate, endDate, days, perDay, currency, service, bookingContactEmail,
    pickupWindow: { date: pw.date, from: pw.from, to: pw.to, timeZone: pw.timeZone },
    noticeHours,
    cancellation: { freeUntil: c.freeUntil, lateFeePercent: c.lateFeePercent },
    guestsRequired,
    requiredGuestFields: fields.filter(isGuestField),
  };
}

/** One person as typed (every field a string while editing; the browser invents no measurement). */
export interface RentalPerson {
  id: string;
  name: string;
  phone: string;
  email: string;
  age: string;
  heightCm: string;
  weightKg: string;
  shoeSize: string;
  ability: string;
}

export const blankRentalPerson = (id: string, contact?: { name?: string | null; email?: string | null; phone?: string | null }): RentalPerson => ({
  id,
  name: contact?.name?.trim() ?? "",
  phone: contact?.phone?.trim() ?? "",
  email: contact?.email?.trim() ?? "",
  age: "",
  heightCm: "",
  weightKg: "",
  shoeSize: "",
  ability: "",
});

const wholeText = (v: string, min: number, max: number) => /^\d+$/.test(v.trim()) && Number(v) >= min && Number(v) <= max;

/** Plain-English messages for the person's fields, "" when the field is fine. The backend still validates every value. */
export function rentalPersonErrors(p: RentalPerson): Record<RentalGuestField, string> {
  const t = (v: string) => v.trim();
  return {
    name: t(p.name) && t(p.name).length <= 120 ? "" : "Enter the guest's full name.",
    phone: /^\+?[0-9 ().-]{7,32}$/.test(t(p.phone)) ? "" : "Enter a phone number, for example 604 555 0100.",
    email: EMAIL.test(t(p.email)) && t(p.email).length <= 254 ? "" : "Enter an email address, for example name@example.com.",
    age: wholeText(p.age, 1, 120) ? "" : "Enter the guest's age in years (1 to 120).",
    heightCm: wholeText(p.heightCm, 50, 250) ? "" : "Enter height in centimetres (50 to 250).",
    weightKg: wholeText(p.weightKg, 10, 250) ? "" : "Enter weight in kilograms (10 to 250).",
    shoeSize: /^[0-9A-Za-z ./-]{1,12}$/.test(t(p.shoeSize)) ? "" : "Enter a shoe size, for example 9 or 27.5.",
    ability: (RENTAL_ABILITIES as readonly string[]).includes(p.ability) ? "" : "Choose the guest's ability level.",
  };
}

/**
 * Field errors for the people some item USES, with their 1-based display order. A person no item uses (including the first guest the
 * account prefilled) is neither sent nor checked, so an unused guest's blank sizing fields never block Pay.
 */
export function usedRentalPeopleErrors(assignments: RentalAssignments, people: RentalPerson[]): { person: RentalPerson; index: number; errors: Record<RentalGuestField, string> }[] {
  const used = new Set(Object.values(assignments).flat().filter(Boolean));
  return people.flatMap((person, index) => (used.has(person.id) ? [{ person, index, errors: rentalPersonErrors(person) }] : []));
}

/** Who uses each unit of each rented line: lineId → one person id per unit ("" = not chosen yet). */
export type RentalAssignments = Record<string, string[]>;

/** One unit's guest as the backend takes it: the full entry the first time a person appears, `{key}` after that. */
export type RentalGuestEntry =
  | { key: string }
  | { key: string; name: string; phone: string; email: string; age: number; heightCm: number; weightKg: number; shoeSize: string; ability: string };

/** A per-unit choice problem, "" when fine. A person may take one unit of a line, never two. */
export function rentalAssignmentErrors(rentals: Record<string, RentalTerms>, assignments: RentalAssignments, people: RentalPerson[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [lineId, terms] of Object.entries(rentals)) {
    const picks = assignments[lineId] ?? [];
    for (let i = 0; i < terms.guestsRequired; i++) {
      const pick = picks[i] ?? "";
      const key = `${lineId}#${i}`;
      if (!pick || !people.some((p) => p.id === pick)) out[key] = "Choose who will use this item.";
      else if (picks.slice(0, i).includes(pick)) out[key] = "Each guest can use only one of these. Choose a different guest.";
      else out[key] = "";
    }
  }
  return out;
}

/**
 * The guests for the ORDER request, per rented line, or null while anything is incomplete. Each person used is entered in full once
 * (under a stable key) and referenced by `{key}` on every later unit, so the backend stores one record per person.
 */
export function buildRentalGuests(rentals: Record<string, RentalTerms>, assignments: RentalAssignments, people: RentalPerson[]): Record<string, RentalGuestEntry[]> | null {
  if (Object.values(rentalAssignmentErrors(rentals, assignments, people)).some(Boolean)) return null;
  const sent = new Set<string>();
  const out: Record<string, RentalGuestEntry[]> = {};
  for (const [lineId, terms] of Object.entries(rentals)) {
    const entries: RentalGuestEntry[] = [];
    for (const pick of (assignments[lineId] ?? []).slice(0, terms.guestsRequired)) {
      const p = people.find((x) => x.id === pick);
      if (!p || Object.values(rentalPersonErrors(p)).some(Boolean)) return null;
      if (sent.has(p.id)) {
        entries.push({ key: p.id });
        continue;
      }
      sent.add(p.id);
      entries.push({
        key: p.id,
        name: p.name.trim(),
        phone: p.phone.trim(),
        email: p.email.trim(),
        age: Number(p.age),
        heightCm: Number(p.heightCm),
        weightKg: Number(p.weightKg),
        shoeSize: p.shoeSize.trim(),
        ability: p.ability,
      });
    }
    out[lineId] = entries;
  }
  return out;
}

const GUEST_KEY = /^[A-Za-z0-9_-]{1,40}$/;

/**
 * The proxy's bound on what the browser sent as one order line's guests: exactly `quantity` entries, each either `{key}` or the eight
 * fields (+ optional key) with nothing else carried through. Values are re-validated by the backend; this only refuses shapes and
 * drops unknown fields. Null = refuse the request.
 */
export function boundRentalGuestEntries(raw: unknown, quantity: unknown): RentalGuestEntry[] | null {
  if (!Array.isArray(raw) || typeof quantity !== "number" || raw.length !== quantity || raw.length > 100) return null;
  const out: RentalGuestEntry[] = [];
  for (const e of raw) {
    if (!isObj(e) || typeof e.key !== "string" || !GUEST_KEY.test(e.key)) return null;
    if (Object.keys(e).length === 1) {
      out.push({ key: e.key });
      continue;
    }
    const { name, phone, email, age, heightCm, weightKg, shoeSize, ability } = e;
    if (![name, phone, email, shoeSize, ability].every((v) => typeof v === "string" && v.length <= 254)) return null;
    if (![age, heightCm, weightKg].every((v) => typeof v === "number" && Number.isInteger(v))) return null;
    out.push({ key: e.key, name, phone, email, age, heightCm, weightKg, shoeSize, ability } as RentalGuestEntry);
  }
  return out;
}

/**
 * The quote's public rental terms, keyed by the design line they belong to — {} when no quote line states any, null (refuse) when any
 * does and the answer can't be tied EXACTLY to what was asked. The quote's lines come back in request order (transport first, then
 * products); each product line must be that product (`product <documentId>`), at that quantity, and a rental's terms must be for
 * exactly the submitted dates and one guest per unit. Only the allowlisted terms survive (`sanitizeRentalTerms`): no internal note,
 * commission or guest data is ever passed on. Never a name, price or position guess.
 */
export function rentalTermsByLine(rawLines: unknown, transportCount: number, products: DesignOrderProduct[]): Record<string, RentalTerms> | null {
  if (!Array.isArray(rawLines)) return {};
  const said = rawLines.some((l) => !!l && typeof l === "object" && (l as { rental?: unknown }).rental !== undefined);
  if (!said) return {};
  if (rawLines.length !== transportCount + products.length) return null;
  if (rawLines.slice(0, transportCount).some((l) => (l as { rental?: unknown })?.rental !== undefined)) return null;
  const out: Record<string, RentalTerms> = {};
  for (const [i, p] of products.entries()) {
    const line = rawLines[transportCount + i] as { kind?: unknown; label?: unknown; quantity?: unknown; rental?: unknown } | null;
    if (line?.kind !== "product" || line.label !== `product ${String(p.productDocumentId ?? "")}` || line.quantity !== p.quantity) return null;
    if (line.rental === undefined) continue;
    const terms = sanitizeRentalTerms(line.rental);
    if (!terms || terms.startDate !== p.useStartDate || terms.endDate !== p.useEndDate || terms.guestsRequired !== p.quantity) return null;
    if (typeof p.lineId !== "string" || !p.lineId || out[p.lineId]) return null;
    out[p.lineId] = terms;
  }
  return out;
}
